/**
 * The phone drawn around a framed preview: the body behind the screen, and the Dynamic Island and
 * home indicator over it (US 359).
 *
 * Siblings of the preview iframe, never a wrapper around it: the published page restyles its one
 * iframe rather than remounting it, so the visitor keeps whatever screen they had reached. Both
 * overlays ignore the pointer, so taps and hot-edit selection reach the app underneath.
 */

import React from 'react';
import type { DeviceFrameLayout } from './devices';

/** Rendered before the iframe, so the screen sits on top of the bezel. */
export function DeviceBody({ layout }: { layout: DeviceFrameLayout }) {
  if (layout.bodyRadius == null) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-0 top-0 bg-neutral-900 shadow-2xl ring-1 ring-black/40"
      style={{
        width: layout.outer.width,
        height: layout.outer.height,
        borderRadius: layout.bodyRadius,
      }}
    />
  );
}

/** Rendered after the iframe, so the island and the home indicator sit on the screen. */
export function DeviceScreenHardware({ layout }: { layout: DeviceFrameLayout }) {
  return (
    <>
      {layout.island && <StatusBar layout={layout} island={layout.island} />}
      {layout.island && (
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-full bg-black"
          style={layout.island}
        />
      )}
      {layout.homeIndicator && (
        // Dark on a light app and light on a dark one, as the real indicator is.
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-full bg-white mix-blend-difference"
          style={{ ...layout.homeIndicator, opacity: 0.55 }}
        />
      )}
    </>
  );
}

/**
 * The clock and the signal/Wi‑Fi/battery glyphs in the top safe area, flanking the island — the
 * frame draws them the way iOS draws the status bar over the app, so the safe area the app leaves
 * free reads as a real status bar rather than empty space. White + difference blend, so it is dark
 * on a light app and light on a dark one, like the home indicator.
 */
function StatusBar({
  layout,
  island,
}: {
  layout: DeviceFrameLayout;
  island: NonNullable<DeviceFrameLayout['island']>;
}) {
  const centerY = island.top + island.height / 2;
  const leftCenter = (layout.screen.left + island.left) / 2;
  const rightCenter = (island.left + island.width + layout.screen.left + layout.screen.width) / 2;
  const fontSize = island.height * 0.42;
  const icon = island.height * 0.34;
  const group = 'pointer-events-none absolute flex items-center text-white mix-blend-difference';
  const anchor = (left: number) => ({ left, top: centerY, transform: 'translate(-50%, -50%)' as const });
  return (
    <>
      <div
        aria-hidden
        className={group}
        style={{ ...anchor(leftCenter), fontSize, fontWeight: 600, letterSpacing: '0.01em' }}
      >
        9:41
      </div>
      <div aria-hidden className={group} style={{ ...anchor(rightCenter), gap: icon * 0.42 }}>
        <SignalGlyph size={icon} />
        <WifiGlyph size={icon} />
        <BatteryGlyph size={icon} />
      </div>
    </>
  );
}

function SignalGlyph({ size }: { size: number }) {
  return (
    <svg width={size * 1.5} height={size} viewBox="0 0 18 12" fill="currentColor" aria-hidden>
      <rect x="0" y="8.5" width="3" height="3.5" rx="1" />
      <rect x="5" y="6" width="3" height="6" rx="1" />
      <rect x="10" y="3.3" width="3" height="8.7" rx="1" />
      <rect x="15" y="0.5" width="3" height="11.5" rx="1" />
    </svg>
  );
}

function WifiGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size * 1.4}
      height={size}
      viewBox="0 0 16 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M2 5.2A9 9 0 0 1 14 5.2" />
      <path d="M4.4 7.9A5.5 5.5 0 0 1 11.6 7.9" />
      <circle cx="8" cy="10.3" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function BatteryGlyph({ size }: { size: number }) {
  return (
    <svg width={size * 2} height={size} viewBox="0 0 25 12" fill="none" aria-hidden>
      <rect x="0.8" y="1" width="20" height="10" rx="2.6" stroke="currentColor" strokeWidth="1" opacity="0.45" />
      <rect x="2.3" y="2.5" width="14.5" height="7" rx="1.4" fill="currentColor" />
      <path d="M23 4.3v3.4a1.8 1.8 0 0 0 0-3.4z" fill="currentColor" opacity="0.45" />
    </svg>
  );
}
